import { forwardRef, useImperativeHandle, useRef } from "react";
import HeroContent from "./HeroContent";

export interface HeroMagnifierHandle {
  update: (x: number, y: number, visible: boolean) => void;
}

interface HeroMagnifierProps {
  lensRadius: number;
  zoom?: number;
}

/**
 * Magnifier lens. The parent (Hero) owns the cursor tracking and drives us via
 * the imperative `update(x, y, visible)` handle, so the mask on the base
 * content and the lens position always agree pixel-perfectly.
 */
const HeroMagnifier = forwardRef<HeroMagnifierHandle, HeroMagnifierProps>(
  function HeroMagnifier({ lensRadius, zoom = 1.8 }, ref) {
    const clipLayerRef = useRef<HTMLDivElement | null>(null);
    const scaleLayerRef = useRef<HTMLDivElement | null>(null);
    const glassRef = useRef<HTMLDivElement | null>(null);

    useImperativeHandle(ref, () => ({
      update(x: number, y: number, visible: boolean) {
        if (clipLayerRef.current) {
          clipLayerRef.current.style.opacity = visible ? "1" : "0";
          clipLayerRef.current.style.clipPath = `circle(${lensRadius}px at ${x}px ${y}px)`;
        }
        if (scaleLayerRef.current) {
          scaleLayerRef.current.style.transformOrigin = `${x}px ${y}px`;
          scaleLayerRef.current.style.transform = `scale(${zoom})`;
        }
        if (glassRef.current) {
          glassRef.current.style.opacity = visible ? "1" : "0";
          glassRef.current.style.transform = `translate3d(${x - lensRadius}px, ${y - lensRadius}px, 0)`;
        }
      },
    }));

    return (
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        {/* Clip layer: circle at cursor */}
        <div
          ref={clipLayerRef}
          className="absolute inset-0 transition-opacity duration-200"
          style={{ opacity: 0, willChange: "clip-path, opacity" }}
        >
          {/* Scale layer: magnify with origin at cursor */}
          <div
            ref={scaleLayerRef}
            className="absolute inset-0"
            style={{ willChange: "transform" }}
          >
            <div className="absolute inset-0 flex items-center justify-center">
              <HeroContent magnified />
            </div>
          </div>
        </div>

        {/* Glass ring + chromatic aberration edges */}
        <div
          ref={glassRef}
          className="absolute top-0 left-0 rounded-full transition-opacity duration-200 hero-lens-glass"
          style={{
            width: `${lensRadius * 2}px`,
            height: `${lensRadius * 2}px`,
            opacity: 0,
            willChange: "transform, opacity",
          }}
        >
          {/* Chromatic aberration — red / green / blue rings slightly offset */}
          <div className="absolute inset-0 rounded-full pointer-events-none hero-lens-chroma-r"></div>
          <div className="absolute inset-0 rounded-full pointer-events-none hero-lens-chroma-g"></div>
          <div className="absolute inset-0 rounded-full pointer-events-none hero-lens-chroma-b"></div>

          {/* Rainbow rim that slowly rotates around the lens */}
          <div className="absolute inset-0 rounded-full pointer-events-none hero-lens-rainbow-rim"></div>

          {/* Glass gloss highlight (top-left) */}
          <div
            className="absolute inset-0 rounded-full pointer-events-none"
            style={{
              background:
                "radial-gradient(circle at 32% 26%, oklch(var(--background-50) / 0.55) 0%, transparent 42%)",
            }}
          ></div>

          {/* Thin outer highlight ring */}
          <div className="absolute inset-0 rounded-full ring-1 ring-background-50/50 pointer-events-none"></div>
        </div>
      </div>
    );
  },
);

export default HeroMagnifier;